import { useEffect, useMemo, useRef } from 'react';
import className from 'classnames/bind';
import { Splide, SplideSlide, SplideTrack } from 'react-splide-ts';
import { CarouselPagination, Container, RevealWrapper, SectionHeader } from '@components';
import { GetAdjacentClassesQuery } from '@graphqlTypes';
import { groupInducteesByYear, replaceStringVar } from '@/core/utils';
import CarouselClassesCard from './Card';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './CarouselClasses.module.scss';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

interface CarouselClassesProps {
  inductees: GetAdjacentClassesQuery['adjacentInducteeClasses'];
}

export default function CarouselClasses({ inductees }: CarouselClassesProps) {
  const rootEl = useRef<HTMLElement>(null);
  const groupedInductees = groupInducteesByYear(inductees);
  const { labels } = usePageWrapperData();
  const { classOfLabel } = labels || {};
  const cta = labels?.carouselClassesCta;
  const headline = labels?.carouselClassesHeadline;

  const years = useMemo(() => {
    return Object.keys(groupedInductees).sort((a, b) => {
      const yearA = parseInt(a, 10);
      const yearB = parseInt(b, 10);
      return yearB - yearA;
    });
  }, [groupedInductees]);

  // -------------------- Splide

  const splideOptions = {
    type: 'slide',
    arrows: true,
    pagination: true,
    speed: 500,
    flickMaxPages: 1,
    flickPower: 250,
    updateOnMove: true,

    // Desktop first (Splide default)
    perPage: 3,
    perMove: 3,
    fixedWidth: '30%',
    gap: '3rem',
    padding: {
      left: '5rem',
      right: '5rem',
    },

    // Responsive options (smaller than desktop)
    breakpoints: {
      // Below 1025px
      1025: {
        perPage: 2,
        perMove: 2,
        fixedWidth: '45%',
        gap: '3rem',
        padding: {
          left: '4rem',
          right: '4rem',
        },
        arrows: false
      },
      // Below 769px
      769: {
        perPage: 1,
        perMove: 1,
        fixedWidth: '76%',
        gap: '2rem',
        padding: {
          left: '2rem',
          right: '2rem',
        },
      },
    }
  };

  // Override accessibility attributes
  useEffect(() => {
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  }, []);

  // Reveal animation
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  return (
    <section className={cx(["carousel-classes"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          <SectionHeader
            headline={headline}
            isRevealEnabled={true}
            isInView={isInView}
          />
        </Container>
        <Splide
          hasTrack={false}
          options={splideOptions}
        >
          <SplideTrack>
            {years.map((year) => (
              <SplideSlide key={year} aria-label={replaceStringVar(classOfLabel, { year })}>
                <CarouselClassesCard
                  key={year}
                  year={year}
                  inductees={groupedInductees[year]}
                />
              </SplideSlide>
            ))}
          </SplideTrack>
          <Container>
            <CarouselPagination className={cx("carousel-pagination")} cta={cta} />
          </Container>
        </Splide>
      </RevealWrapper>
    </section>
  )
}